import React from 'react';
import styled, { css } from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import FAIcon from '#/common/components/FAIcon';
import ToolbarButton from '../DataGrid/ToolbarButton';
import { isLightTheme } from '#/common/utils/themeDetection';
import { FileInfo, FileStatus } from './FileUploadProps';

export const CardStyles = css<{ width: string }>`
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: ${({ theme }) =>
    isLightTheme(theme) ? color('ui', 'light3') : color('ui', 'middle')};
  border-radius: 16px;
  width: ${({ width }) => width};
  padding: 12px;
`;

const FileInfoCardStyles = styled.div<{ width: string }>`
  ${CardStyles}
  max-height: 62px;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100% !important;
  }
`;

const FileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  pointer-events: ${({ onClick }) => (onClick ? 'auto' : 'none')};
`;

const FileMetadata = styled.div`
  font-size: 12px;
  color: ${color('secondary', 'dark')};
`;

const FileName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  min-width: 0;
`;

const DeleteButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: auto;

  .dx-button.dx-widget {
    width: 44px !important;
    height: 44px;
    min-width: 44px;
    padding: 0;
  }
  .dx-button-content {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const IconWrapper = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background-color: ${({ theme }) =>
    isLightTheme(theme) ? color('ui', 'light2') : color('ui', 'dark')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  pointer-events: ${({ onClick }) => (onClick ? 'auto' : 'none')};
`;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const fileIcons = {
  image: ['image', 'jpeg', 'png', 'gif', 'bmp', 'svg'],
  pdf: ['pdf', 'adobe', 'acrobat'],
  excel: ['excel', 'spreadsheetml', 'spreadsheet', 'csv', 'xls', 'xlsx'],
  xml: ['xml'],
  word: ['word', 'document', 'doc', 'docx'],
  powerpoint: ['powerpoint', 'ppt', 'pptx'],
  lines: ['txt', 'text'],
  zipper: ['zip', 'compressed', 'rar', '7z'],
  markdown: ['markdown', 'md'],
  audio: ['audio', 'mp3', 'wav', 'ogg'],
  video: ['video', 'mp4', 'avi', 'mov', 'mkv'],
  cad: ['cad', 'dwg', 'dxf'],
};

const getIcon = (mimeType: string | undefined): string => {
  if (!mimeType) {
    return 'file';
  }

  const iconEntry = Object.entries(fileIcons).find(([icon, mimeTypes]) =>
    mimeTypes.some((type) => mimeType.includes(type)),
  );

  return iconEntry ? `file-${iconEntry[0]}` : 'file';
};

const statusesToShow = new Set<FileStatus>(['uploading', 'deleting', 'opening']);

export interface FileInfoCardProps extends FileInfo {
  onOpen?: () => void;
  onDelete?: () => void;
  width?: string;
  deleteTxt?: string;
}

const FileInfoCard: React.FC<FileInfoCardProps> = ({
  name,
  size,
  mimeType,
  status,
  onOpen,
  onDelete,
  width = 'calc((100% - 88px) / 3)',
  deleteTxt = 'Delete',
}) => {
  const canOpen = !!onOpen && status === 'uploaded';
  const metadata = [];
  if (size !== undefined) {
    metadata.push(formatFileSize(size));
  }
  if (status && statusesToShow.has(status)) {
    if (status === 'opening') {
      metadata.push('loading...');
    } else {
      metadata.push(status + '...');
    }
  }
  return (
    <FileInfoCardStyles width={width}>
      <IconWrapper onClick={canOpen ? onOpen : undefined}>
        <FAIcon icon={getIcon(mimeType)} />
      </IconWrapper>
      <FileDetails onClick={canOpen ? onOpen : undefined}>
        <NuvoLabel>
          <FileName>{name}</FileName>
        </NuvoLabel>
        {metadata.length > 0 && <FileMetadata>{metadata.join(' | ')}</FileMetadata>}
      </FileDetails>

      {onDelete && status !== 'uploading' && (
        <DeleteButtonWrapper>
          <ToolbarButton
            tooltip={deleteTxt}
            icon="trash"
            type="danger"
            onClick={onDelete}
            disabled={status === 'deleting'}
          />
        </DeleteButtonWrapper>
      )}
    </FileInfoCardStyles>
  );
};

export default React.memo(FileInfoCard);
